import '@repo/ui/fonts';
import { arSD as coreAr, enUS as coreEn, frFR as coreFr } from '@mui/material/locale';
import { arSD as gridAr, enUS as gridEn, frFR as gridFr } from '@mui/x-data-grid/locales';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { enUS as pickersEn, frFR as pickersFr } from '@mui/x-date-pickers/locales';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import 'dayjs/locale/ar-tn';
import 'dayjs/locale/fr';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@repo/shared';
import { AppThemeProvider, ToastProvider } from '@repo/ui';
import { TenantProvider, tenantQuery } from './app/tenant';
import { directionOf, setLocale, storedLocale } from './i18n';
import { isApiError } from './lib/api';
import { queryClient } from './lib/query';
import { BootError } from './pages/StatusPages';
import { createAppRouter } from './routes/router';

/**
 * Server-mode grids report 0 rows until the first page arrives: the footer stays empty instead of reading
 * « 0–0 sur 0 » while loading (and when a filter returns nothing, the empty state says it already).
 */
function quietEmptyCount(core: typeof coreFr): object {
  const base = core.components?.MuiTablePagination?.defaultProps;
  const label = base?.labelDisplayedRows;
  if (!base || !label) return {};
  return {
    components: {
      MuiDataGrid: {
        defaultProps: {
          localeText: {
            MuiTablePagination: {
              ...base,
              labelDisplayedRows: (p: Parameters<typeof label>[0]) => (p.count > 0 ? label(p) : ''),
            },
          },
        },
      },
    },
  };
}

const MUI_LOCALES: Record<Locale, object[]> = {
  fr: [coreFr, gridFr, pickersFr, quietEmptyCount(coreFr)],
  // MUI X Date Pickers has no Arabic locale yet: English picker texts (see TODO-i18n.md).
  ar: [coreAr, gridAr, pickersEn, quietEmptyCount(coreAr)],
  en: [coreEn, gridEn, pickersEn, quietEmptyCount(coreEn)],
};
const DAYJS: Record<Locale, string> = { fr: 'fr', ar: 'ar-tn', en: 'en' };

const routers = new Map<string, ReturnType<typeof createAppRouter>>();
const routerFor = (kind: 'tenant' | 'platform') => {
  let r = routers.get(kind);
  if (!r) {
    r = createAppRouter(kind, queryClient);
    routers.set(kind, r);
  }
  return r;
};

function Root() {
  const { i18n, t } = useTranslation();
  const locale = (['fr', 'ar', 'en'].includes(i18n.language) ? i18n.language : 'fr') as Locale;
  const tenant = useQuery(tenantQuery);
  const locales = useMemo(() => MUI_LOCALES[locale], [locale]);
  const domain = window.location.hostname.split('.').slice(1).join('.') || window.location.hostname;

  return (
    <AppThemeProvider primary={tenant.data?.primaryColor} direction={directionOf(locale)} locales={locales}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={DAYJS[locale]}>
        <ToastProvider closeLabel={t('common.close')}>
          <TenantProvider
            fallback={(error) => (
              <BootError
                kind={isApiError(error, 'TENANT_NOT_FOUND') ? 'tenant' : 'network'}
                domain={domain}
                onRetry={() => void tenant.refetch()}
              />
            )}
          >
            {(tn) => <RouterProvider router={routerFor(tn.kind)} />}
          </TenantProvider>
        </ToastProvider>
      </LocalizationProvider>
    </AppThemeProvider>
  );
}

void setLocale(storedLocale() ?? 'fr');

const el = document.getElementById('root');
if (el) {
  createRoot(el).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <Root />
      </QueryClientProvider>
    </StrictMode>,
  );
}
